/**
 * Rota /ecommerce/gerenciar/:patientKey/:transactionId
 * Resolve o paciente (state ou consentimento) e monta a página.
 * @module ManageDocumentsReleaseRoute
 */

import { memo } from "react";
import { Alert, Button, Stack } from "@mui/material";
import type { Patient } from "fhir/r5";
import { useLocation, useParams } from "react-router-dom";
import { oids } from "@/configs/Settings";
import { LargeContentBox } from "@/components/common";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import {
  parseEcommerceManageSplat,
  useEcommerceManagePatient,
  type EcommerceManageLocationState,
} from "src-next/modules/ecommerce";
import ManageDocumentsReleasePage from "./index";
import { ManageDocumentsReleaseSkeleton } from "./ManageDocumentsReleaseSkeleton";

interface ManageDocumentsReleaseRouteProps {
  organizationId?: string;
  onClose: () => void;
  onSendWhatsApp?: (
    patients: Patient[],
    options?: { onSent?: () => void },
  ) => void;
}

export const ManageDocumentsReleaseRoute = memo(
  function ManageDocumentsReleaseRoute({
    organizationId,
    onClose,
    onSendWhatsApp,
  }: ManageDocumentsReleaseRouteProps) {
    const { "*": splat } = useParams();
    const location = useLocation();
    const { organization } = useOrganizationContext();
    const splatFromPath = location.pathname.replace(/^\/ecommerce\/?/, "");
    const routeParams = parseEcommerceManageSplat(splat || splatFromPath);
    const navigationState = location.state as
      | EcommerceManageLocationState
      | null;

    const cnpj = organization?.identifier?.find((identifier) =>
      identifier.system?.includes(oids.cnpj),
    )?.value;

    const { patient, isLoading, isMissing } = useEcommerceManagePatient({
      patientKey: routeParams.patientKey,
      cnpj,
      patientFromState: navigationState?.patient,
    });

    if (isLoading) {
      return <ManageDocumentsReleaseSkeleton />;
    }

    if (isMissing || !patient) {
      return (
        <LargeContentBox>
          <Stack spacing={2} sx={{ width: "100%" }}>
            <Alert
              severity="warning"
              action={
                <Button color="inherit" size="small" onClick={onClose}>
                  Voltar para a lista
                </Button>
              }
            >
              Paciente não encontrado ou sem consentimento nesta organização.
            </Alert>
          </Stack>
        </LargeContentBox>
      );
    }

    return (
      <LargeContentBox>
        <ManageDocumentsReleasePage
          patient={patient}
          organizationId={organizationId}
          transactionId={routeParams.transactionId}
          onClose={onClose}
          onSendWhatsApp={onSendWhatsApp}
        />
      </LargeContentBox>
    );
  },
);
